import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@core/utils/cn';
import type { Language } from '@core/i18n';

/** Short enough to sit in a segment, and each is the name of the language in
 *  itself — a reader who wants Arabic recognises `ع` without reading English. */
const CHIP: Readonly<Record<Language, string>> = { en: 'EN', ar: 'ع' };

const LANGUAGES: readonly Language[] = ['en', 'ar'];

/** How long the drop stays stretched. Shorter than the slide on purpose: the
 *  stretch belongs to the launch, and the settle is what the eye lands on. */
const STRETCH_MS = 240;

/**
 * Both languages, with the current one lit.
 *
 * A toggle rather than the button this was: that one showed the language you
 * would GET, which reads as a label for the state you are in — an English
 * reader saw "العربية" and had to work out whether it was describing the
 * current setting or offering the other one. Showing both and lighting one
 * removes the question.
 *
 * The lit half is one circle exactly as tall as the pill and exactly half as
 * wide, so its radius equals the pill's own end cap and the two align with no
 * clipping — which is also why there is no `overflow-hidden` here to swallow a
 * focus ring.
 *
 * It moves like a drop: the slide overshoots and settles on a spring curve
 * while the circle briefly stretches along its direction of travel and flattens
 * against it. Squash-and-stretch is the whole trick; the two are separate CSS
 * properties precisely so the stretch can end well before the slide does.
 *
 * Pinned to `dir="ltr"`. Every other row in the app flips with the writing
 * direction and should; this one must not, because the two segments would swap
 * places at the exact moment they were pressed — you would click the segment on
 * the right and watch your choice land on the left. It is also what lets the
 * travel below be a plain leftward-to-rightward offset.
 */
export function LanguageToggle(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const current: Language = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  const [stretching, setStretching] = useState(false);
  const settled = useRef(false);

  // Only on a CHANGE. Without the ref the drop would squash once on mount, so
  // every page load would open with an animation nobody asked for.
  useEffect(() => {
    if (!settled.current) {
      settled.current = true;
      return undefined;
    }
    setStretching(true);
    const timer = window.setTimeout(() => {
      setStretching(false);
    }, STRETCH_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [current]);

  return (
    <div
      role="group"
      aria-label={t('nav.language')}
      dir="ltr"
      className="relative inline-flex items-center rounded-full border border-[var(--btn-ghost-bd)]"
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 size-9 rounded-full',
          'bg-accent/20 shadow-[0_0_12px_-4px_var(--accent)]',
          // Two properties, two clocks: the slide springs past its mark and
          // settles back over half a second; the stretch is gone in a quarter of
          // one. `motion-safe`, so anyone who asked for calm gets the jump cut.
          'motion-safe:[transition:translate_520ms_cubic-bezier(0.34,1.56,0.64,1),scale_240ms_ease-out]',
          current === 'ar' ? '[translate:2.25rem_0]' : '[translate:0_0]',
          // Only the stretched state is a class. Rest is the ABSENCE of one —
          // removing it lets the transition interpolate back to the property's
          // own default, and spares a `scale:1 1` rule that Tailwind declines to
          // emit anyway. A class that generates no CSS is a lie in the markup.
          stretching && 'motion-safe:[scale:1.18_0.86]',
        )}
      />

      {LANGUAGES.map((language) => {
        const active = language === current;
        return (
          <button
            key={language}
            type="button"
            // A no-op rather than a re-`changeLanguage`: pressing the segment you
            // are already on should not tear down and rebuild the tree, and it
            // should not fire the drop at a target it is already sitting on.
            onClick={() => {
              if (!active) void i18n.changeLanguage(language);
            }}
            aria-pressed={active}
            aria-label={t(`nav.lang.${language}`)}
            title={t(`nav.lang.${language}`)}
            className={cn(
              'relative z-10 flex size-9 items-center justify-center rounded-full',
              'font-mono text-xs outline-none transition-colors',
              'dda-focus',
              active ? 'font-semibold text-accent' : 'text-muted hover:text-text',
            )}
          >
            {CHIP[language]}
          </button>
        );
      })}
    </div>
  );
}
